// React hooks
import { createContext, useContext, useState, useEffect } from "react";
// Skybase hooks
import { useData } from '../context/DataContext.js';
import { useAuth } from '../context/AuthContext.js';
import { API_BASE_URL } from "../config.js";
// External hooks/components
import { useAuth as useClerkAuth } from "@clerk/react";



// Create new React Context
const QueryContext = createContext();



// =============
// QueryProvider – provides GC related to Search-Query processing related data.
// =============
export default function QueryProvider({ children }){

    // destructure GC
    const { isSubscribed } = useAuth();
    const { activeEngineJson } = useData();

    // init hooks
    const { getToken, isSignedIn } = useClerkAuth();


    // ============
    // Active Query (String) – the search query of current priority
    // ============
    // state var
    const [activeQuery, setActiveQuery] = useState(() => {
        
        // retrieve from localStorage otherwise ""
        const saved = localStorage.getItem("activeQuery");
        return saved ? saved : "";
    })
    // localStorage caching
    useEffect(() => {
        localStorage.setItem("activeQuery", activeQuery);
    }, [activeQuery])


    // ===============
    // isKeywordIntent (bool) – is this search keyword-only?
    // ===============
    // state var
    const [isKeywordIntent, setIsKeywordIntent] = useState(() => {
        
        // retrieve from localStorage otherwise FALSE
        const saved = localStorage.getItem("isKeywordIntent");
        return saved !== null ? JSON.parse(saved) : false;
    });
    // localStorage caching
    useEffect(() => {
        localStorage.setItem("isKeywordIntent", JSON.stringify(isKeywordIntent));
    }, [isKeywordIntent])


    // ====================
    // Ranked Query Results – a JSON[] of best-matching search results
    // ====================
    // state var
    const [rankedQueryResults, setRankedQueryResults] = useState(() => {

        // retrieve from localStorage otherwise []
        const saved = localStorage.getItem("rankedQueryResults");
        return saved ? JSON.parse(saved) : [];
    });
    // localStorage caching
    useEffect(() => {
        localStorage.setItem("rankedQueryResults", JSON.stringify(rankedQueryResults));
    }, [rankedQueryResults])
    
    
    // ==========================
    // Search Query Processing () – Async. processes a 'query' (String). fetches new 'rankedQueryResults' (JSON[]).
    // ==========================
    // state var – tracks `isSearching`
    const [isSearching, setIsSearching] = useState(false);
    // processSearchQuery(query: String) – async. processes a 'query' --> fetches new 'rankedQueryResults' (JSON[]).
    async function processSearchQuery(query){

        // toggle ON 'isSearching'
        setIsSearching(true);
        
        // update 'activeQuery' (so it's properly displayed on the loading screen)
        setActiveQuery(query);
        

        // `GET /search/{engine_id}`
        const token = await getToken();
        const url = new URL(`${API_BASE_URL}/api/v1/search/${activeEngineJson.id}`);

        url.searchParams.set("search_query", query);
        url.searchParams.set("is_keyword_intent", isKeywordIntent);

        const results_response = await fetch(url,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        );
        if (!results_response.ok) { throw new Error(`SKYBASE (Client) – Unable to Process Search Query: "${query}"`)};
        const results_json = await results_response.json();
        
        // ⚠️ Developer debugging
        // console.log(`SKYBASE (client): Displaying search results for:\n\n"${query}"`);
        // console.log(results_json);
        
        // update JSON (triggers re-render)
        setRankedQueryResults(results_json);

        // change searchVersion (triggers a useEffect to properly update the 'activeEngineSearchEvents' JSON in real-time)
        setSearchVersion(prev => prev += 1);

        // toggle OFF 'isSearching'
        setIsSearching(false);
        
        // return statement
        return;
    }


    // ===========================
    // ACTIVE ENGINE SEARCH EVENTS
    // ===========================
    // state var
    const [searchVersion, setSearchVersion] = useState(0); // used to forcibly trigger various useEffects that re-fetch() real-time JSON data
    // state var
    const [activeEngineSearchEvents, setActiveEngineSearchEvents] = useState(() => {
        
        // retrieve from localStorage otherwise []
        const saved = localStorage.getItem("activeEngineSearchEvents");
        return saved ? JSON.parse(saved) : [];
    })
    // localStorage caching
    useEffect(() => {
        localStorage.setItem("activeEngineSearchEvents", JSON.stringify(activeEngineSearchEvents));
    }, [activeEngineSearchEvents])
    // fetch() behavior – triggered by various dependencies
    useEffect(() => {
        
        // fetch()
        async function fetchActiveEngineSearchEvents() {
            try {
                // check whether vars exist before running fetch()
                if (!activeEngineJson?.id || !isSignedIn) { return; }

                // Exit condition: Never fetch() a subscribed-route if CurrentUser !isSubscribed.
                if (!isSubscribed) { return; }

                // Exit condition: Never fetch() a subscribed-route if <Clerk JWT> non-existent.
                const token = await getToken();
                if (!token) { return };

                // send fetch() `GET /search/events/{engine_id}?user_id={user_id}`
                const url = new URL(`${API_BASE_URL}/api/v1/search/events/${encodeURIComponent(activeEngineJson.id)}`);

                const response = await fetch(url,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
                if (!response.ok) { throw new Error(`SKYBASE (client) – HTTP ${response.status}: Unable to get SearchEvents for Engine: ${activeEngineJson.id}`)};
                const search_events_json = await response.json();

                // trigger re-render
                setActiveEngineSearchEvents(search_events_json);

            } catch (err) {
                console.error(err);
            }
        }

        // call fetch()
        fetchActiveEngineSearchEvents()
    }, [activeEngineJson, isSignedIn, activeQuery, searchVersion])


    // ==========================
    // SELECTED SEARCH EVENT JSON – a JSON[] containing all Postgres "search_event" information. Used on the /history and /cache page.
    // ==========================
    // state var
    const [selectedSearchResultJson, setSelectedSearchResultJson] = useState(() => {

        // retrieve localStorage cache, otherwise default NULL
        const saved = localStorage.getItem("selectedSearchResultJson");
        return saved ? JSON.parse(saved) : null;
    });
    // localStorage caching
    useEffect(() => {
        localStorage.setItem("selectedSearchResultJson", JSON.stringify(selectedSearchResultJson));
    }, [selectedSearchResultJson]);
    // changeSelectedSearchResultJson(jsonObj: JS Object) --> 'selectedSearchResultJson' SETTER
    function changeSelectedSearchResultJson(jsonObj) {
        setSelectedSearchResultJson(jsonObj);
    }


    // JSX Component – Provider
    return(
        <QueryContext.Provider
        value={{
            activeQuery,                    // String     
            rankedQueryResults,             // JSON[]
            processSearchQuery,             // async Function(String)
            activeEngineSearchEvents,       // JSON[]
            isKeywordIntent,                // bool
            setIsKeywordIntent,             // ()
            changeSelectedSearchResultJson, // ()
            selectedSearchResultJson,       // JSON[]
            isSearching,                    // bool
        }}>
            {children}
        </QueryContext.Provider>
    )
}


// Create the 'useQuery' Skybase hook
export function useQuery() {
    return useContext(QueryContext);
}